/* eslint-disable @kaminrunde/firescout/onclick-needs-handle */
import * as React from 'react'
import { Props } from './types'
import theme from 'theme'
import ProductSizes from '../ProductSizes'
import InternationalSizes from '../InternationalSizes'
import { ReactComponent as CloseIcon } from 'assets/closeOutline.svg'
import ProductInfo from '../ProductInfo'
import InfoBox from '../InfoBox'
import { useModal } from 'modules/ui'
import { styled } from 'styled-components'
import useTranslations from 'hooks/useTranslations'
import { useEffect } from 'react'

export default function ModalContent(props: Props) {
  const t = useTranslations<'templates-PDP'>()
  const modal = useModal()
  const closeBtnRef = React.useRef<HTMLButtonElement>(null)
  const prevFocusEl = React.useRef<HTMLElement | null>(null)

  const handleClose = () => {
    prevFocusEl.current?.classList.contains('size-table-btn')
      ? prevFocusEl.current.focus()
      : document.getElementById('filter-id-size')?.focus()
    modal.setContent(null)
    props.setPdpSizeTableOpen(false, props.context)
  }

  useEffect(() => {
    // Disable scrolling when modal is open
    document.body.style.overflow = 'hidden'
    prevFocusEl.current = document.activeElement as HTMLElement
    closeBtnRef.current?.focus()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup function to reset the overflow style when modal is closed
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <Wrapper className="ModalContent" data-cy-collection="ModalContent">
      <Modal
        className="modal"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        <button
          ref={closeBtnRef}
          className="close-icon"
          data-cy-handle="close-size-table-modal"
          onClick={() => handleClose()}
          // TODO: bfsg snippet
          title="close size table modal"
        >
          <CloseIcon />
        </button>
        <div className="content">
          {
            // Only render the product sizes chart if there is data available
            props.sizeChartAttributes.size > 0 && (
              <div className="product_sizes" data-cy-state="show-product-sizes">
                <ProductSizes
                  sizeChartData={props.variant.data?.sizeChartData}
                />

                <InfoBox title={t('sizeChart_measurement_infobox_title')}>
                  <ol className="info_box_ol">
                    <li>{t('sizeChart_measurement_infobox_text1')}</li>
                    <li>{t('sizeChart_measurement_infobox_text2')}</li>
                  </ol>
                </InfoBox>

                <ProductInfo
                  sizeChartLogoUrl={props.sizeChartLogoUrl}
                  sizeChartAttributes={props.sizeChartAttributes}
                />
              </div>
            )
          }

          <InternationalSizes
            sizeChartData={props.variant.data?.sizeChartData}
          />
        </div>
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing('s')};

  @media (min-width: ${theme.ms.LG}px) {
    padding: ${theme.spacing('l')};
  }

  overflow-y: auto;

  @media (min-width: ${theme.ms.LG}px) {
    max-width: 60%;
  }
  max-height: 85%;
`

const Modal = styled.div`
  > .close-icon {
    text-align: end;
    cursor: pointer;
  }

  > .content {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('m')};

    > .modal-headline {
      ${theme.ty('rc-xl', '700', 'r-2xl')}
      margin-bottom: ${theme.spacing('m')};
    }
    > .info-list {
      list-style: inside decimal;
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing('s')};
      > li {
        ${theme.ty('r-s', null, 'r-base')}
      }
    }

    // Styling the ol inside the InfoBox.
    // Using > would need to use implementation details from the info box.
    .info_box_ol {
      margin: revert;
      padding-left: ${theme.spacing('m')};
      list-style: revert;
    }

    > .product_sizes {
      > .InfoBox {
        margin-top: ${theme.spacing('s')};
        margin-bottom: ${theme.spacing('s')};
      }
    }
  }
`
